import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  HostListener,
  NgZone,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatSort, Sort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';
import { AssetPickupData } from '../../types';
import { AssetsService } from '../assets.service';
import { AssetSelectionDialogComponent } from './asset-selection-dialog/asset-selection-dialog.component';
import { CancelPickupDialogComponent } from './cancel-pickup-dialog/cancel-pickup-dialog.component';

// Mobile breakpoint matching the SCSS variable
const MOBILE_BREAKPOINT = 768;

@Component({
  selector: 'lohi-asset-pickup-queue',
  templateUrl: './asset-pickup-queue.component.html',
  styleUrls: ['./asset-pickup-queue.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AssetPickupQueueComponent implements OnInit, AfterViewInit, OnDestroy {
  public displayedColumns: string[] = [
    'driverName',
    'phoneNumber',
    'companyName',
    'scheduledAt',
    'assetName',
    'pickupSiteName',
    'status',
    'sufficientEscrow',
    'actions',
  ];
  public dataSource: MatTableDataSource<AssetPickupData>;
  public totalItems = 0;
  public pageSize = 10;
  public currentPage = 0;
  public sortDirection = 'ASC';
  public sortField = 'driverName';
  public searchTerm = '';
  public isLoading = false;
  public isMobile = false;

  private searchSubject$ = new Subject<string>();
  private destroy$ = new Subject<void>();
  private resizeTimeout: any = null;

  @ViewChild(MatPaginator) public paginator!: MatPaginator;
  @ViewChild(MatSort) public sort!: MatSort;

  constructor(
    private assetsService: AssetsService,
    private cdr: ChangeDetectorRef,
    private dialog: MatDialog,
    private snackBar: MatSnackBar,
    private zone: NgZone,
  ) {
    // Initialize the data source with empty array
    this.dataSource = new MatTableDataSource<AssetPickupData>([]);

    // Check initial screen size
    this.checkScreenSize();
  }

  @HostListener('window:resize')
  public onResize(): void {
    // Debounce resize events to avoid excessive calls
    if (this.resizeTimeout) {
      clearTimeout(this.resizeTimeout);
    }

    this.resizeTimeout = setTimeout(() => {
      // Run outside Angular's zone to avoid triggering change detection during a cycle
      this.zone.runOutsideAngular(() => {
        this.checkScreenSize();
      });
    }, 100);
  }

  private checkScreenSize(): void {
    const isMobileNow = window.innerWidth < MOBILE_BREAKPOINT;

    // Only update if the value has changed to avoid unnecessary change detection
    if (this.isMobile !== isMobileNow) {
      this.isMobile = isMobileNow;

      // Re-enter Angular zone when we need to update the UI
      this.zone.run(() => {
        this.cdr.markForCheck(); // Use markForCheck instead of detectChanges
      });
    }
  }

  // Dialog size adjustment based on screen size
  private getDialogConfig(): { width: string } {
    return {
      width: this.isMobile ? '90%' : '400px',
    };
  }

  public ngOnInit(): void {
    // Load initial data
    this.loadServerData(this.currentPage, this.pageSize, this.sortDirection, this.sortField, this.searchTerm);

    // Set up debounced search
    this.searchSubject$
      .pipe(
        debounceTime(500), // Wait 500ms after the last event before emitting
        distinctUntilChanged(), // Only emit if value is different from previous
        takeUntil(this.destroy$),
      )
      .subscribe((searchTerm) => {
        this.searchTerm = searchTerm;

        // Reset to first page when searching
        this.currentPage = 0;
        if (this.paginator) {
          this.paginator.pageIndex = 0;
        }

        // Fetch data with the search term
        this.loadServerData(this.currentPage, this.pageSize, this.sortDirection, this.sortField, this.searchTerm);
      });
  }

  public ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public ngAfterViewInit() {
    // Set up sort change listener
    this.sort.sortChange.subscribe((sort: Sort) => {
      this.sortDirection = sort.direction;
      this.sortField = sort.active;
      this.paginator.pageIndex = 0;
      this.loadServerData(0, this.pageSize, this.sortDirection, this.sortField, this.searchTerm);
    });

    // Set up page change listener
    this.paginator.page.subscribe((pageEvent: PageEvent) => {
      console.log('Page event:', pageEvent);
      this.currentPage = pageEvent.pageIndex;
      this.pageSize = pageEvent.pageSize;
      this.loadServerData(this.currentPage, this.pageSize, this.sortDirection, this.sortField, this.searchTerm);
    });
  }

  public applyFilter(event: Event) {
    const filterValue = (event.target as HTMLInputElement).value;
    // Push the search term to the subject instead of loading directly
    this.searchSubject$.next(filterValue.trim());
  }

  // This method is used for implementing real server-side pagination
  private async loadServerData(
    pageIndex: number,
    pageSize: number,
    sortDirection: string,
    sortField: string,
    searchTerm?: string,
  ) {
    // Add loading indicator
    this.isLoading = true;
    this.cdr.detectChanges();

    try {
      const resp = await this.assetsService.allAssets$(pageIndex, pageSize, sortDirection, sortField, searchTerm);

      this.dataSource.data = resp?.data || [];
      this.totalItems = resp?.totalCount || 0;

      console.log('response', resp);
      console.log('Pagination state:', {
        currentPage: this.currentPage,
        pageSize: this.pageSize,
        totalItems: this.totalItems,
        searchTerm: this.searchTerm,
      });
    } catch (error) {
      console.error('Error loading data:', error);
      this.snackBar.open('Failed to load data. Please try again.', 'Close', {
        duration: 5000,
      });
    } finally {
      this.isLoading = false;

      // Update paginator without triggering another page event
      if (this.paginator) {
        // Temporarily remove the subscription to avoid infinite loop
        const pageSubscription = this.paginator.page.subscribe();
        pageSubscription.unsubscribe();

        this.paginator.pageIndex = pageIndex;
        this.paginator.pageSize = pageSize;
        this.paginator.length = this.totalItems;

        // Force the paginator to update its view
        this.cdr.detectChanges();
      }
    }
  }

  // Menu action handlers
  public assignAsset(row: AssetPickupData): void {
    console.log('Assigning asset for:', row);

    const dialogRef = this.dialog.open(AssetSelectionDialogComponent, {
      ...this.getDialogConfig(),
      data: {
        scheduleId: row.id,
        currentAssetId: row.assetId,
        assetTypes: ['trailer'], // Default to trailer type, can be expanded based on requirements
      },
    });

    dialogRef.afterClosed().subscribe(async (result) => {
      if (result) {
        // Show loading spinner
        this.isLoading = true;
        this.cdr.detectChanges();

        try {
          // Call the API to update the asset
          await this.assetsService.updateScheduledPickupAsset(result.scheduleId, result.assetId);

          // Show success message
          this.snackBar.open('Asset updated successfully', 'Close', {
            duration: 3000,
          });

          // Refresh the data
          this.loadServerData(this.currentPage, this.pageSize, this.sortDirection, this.sortField, this.searchTerm);
        } catch (error) {
          console.error('Error updating asset:', error);
          this.snackBar.open('Failed to update asset. Please try again.', 'Close', {
            duration: 5000,
          });
          this.isLoading = false;
          this.cdr.detectChanges();
        }
      }
    });
  }

  public cancelPickup(row: AssetPickupData): void {
    console.log('Cancelling pickup for:', row);

    const dialogRef = this.dialog.open(CancelPickupDialogComponent, {
      ...this.getDialogConfig(),
      data: {
        scheduleId: row.id,
        assetName: row.assetName,
        driverName: row.driverName,
      },
    });

    dialogRef.afterClosed().subscribe(async (result) => {
      if (result) {
        // Show loading spinner
        this.isLoading = true;
        this.cdr.detectChanges();

        try {
          // Call the API to archive the scheduled pickup
          await this.assetsService.archiveScheduledPickup(result.scheduleId, result.reason);

          // Show success message
          this.snackBar.open('Pickup cancelled successfully', 'Close', {
            duration: 3000,
          });

          // Refresh the data
          this.loadServerData(this.currentPage, this.pageSize, this.sortDirection, this.sortField, this.searchTerm);
        } catch (error) {
          console.error('Error cancelling pickup:', error);
          this.snackBar.open('Failed to cancel pickup. Please try again.', 'Close', {
            duration: 5000,
          });
          this.isLoading = false;
          this.cdr.detectChanges();
        }
      }
    });
  }

  // TrackBy function for ngFor performance optimization
  public trackByFn(index: number, item: AssetPickupData): number | string {
    return item.id;
  }
}
